import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  searchDirectory,
  fetchListings,
  fetchCities,
  fetchAreas,
  downloadExport,
  fetchMoreFromDirectory,
} from '../services/directoryScraperService';
import { ErrorTypes } from '../utils/errorHandler';
import SearchableSelect from '../components/common/SearchableSelect';

/** Below this total_count, repeat Search POSTs with force_refresh to merge more from Google Places. */
const LOW_RESULT_THRESHOLD = 50;

/** After this long stuck on "running", show stalled message (worker may be down or job hung). */
const BACKGROUND_STALL_MS = 90000;

/** Maps column id to [asc, desc] API sort keys (must match backend listing_service.VALID_SORTS). */
const COLUMN_SORT = {
  name: ['name_asc', 'name_desc'],
  area: ['area_asc', 'area_desc'],
  city: ['city_asc', 'city_desc'],
  phone: ['phone_asc', 'phone_desc'],
  address: ['address_asc', 'address_desc'],
  km: ['distance_asc', 'distance_desc'],
};

function mapDirectorySkipReason(reason) {
  if (!reason) return '';
  const m = {
    cooldown: 'Try again after the cooldown period.',
    max_per_session: 'Maximum requests for this search session reached.',
    exhausted: 'No more distinct results from Google for this search.',
  };
  return m[reason] || reason;
}

function buildSearchParamsKey(category, city, area, extraSearch) {
  return JSON.stringify({
    c: (category || '').trim().toLowerCase(),
    city: (city || '').trim().toLowerCase(),
    area: (area || '').trim().toLowerCase(),
    x: (extraSearch || '').trim().toLowerCase(),
  });
}

/** Mobile sort dropdown — mirrors COLUMN_SORT / backend VALID_SORTS. */
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'area_asc', label: 'Area (A–Z)' },
  { value: 'area_desc', label: 'Area (Z–A)' },
  { value: 'city_asc', label: 'City (A–Z)' },
  { value: 'city_desc', label: 'City (Z–A)' },
  { value: 'phone_asc', label: 'Phone (A–Z)' },
  { value: 'phone_desc', label: 'Phone (Z–A)' },
  { value: 'address_asc', label: 'Address (A–Z)' },
  { value: 'address_desc', label: 'Address (Z–A)' },
  { value: 'distance_asc', label: 'Distance (nearest first)' },
  { value: 'distance_desc', label: 'Distance (farthest first)' },
];

/**
 * Renders label/value pairs for a listing row (expanded state).
 * Purpose: single source for mobile cards and desktop expanded rows.
 */
function ListingDetailFields({ row }) {
  return (
    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 sm:gap-x-4">
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Area</dt>
        <dd className="mt-0.5 break-words text-gray-900 dark:text-gray-100">{row.area || '—'}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">City</dt>
        <dd className="mt-0.5 break-words text-gray-900 dark:text-gray-100">{row.city || '—'}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Phone</dt>
        <dd className="mt-0.5 break-words text-gray-900 dark:text-gray-100">
          {row.phone ? (
            <a
              href={`tel:${String(row.phone).replace(/\s/g, '')}`}
              className="text-primary-600 underline dark:text-primary-400"
            >
              {row.phone}
            </a>
          ) : (
            '—'
          )}
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Distance</dt>
        <dd className="mt-0.5 text-gray-900 dark:text-gray-100">
          {row.distance_km != null ? `${row.distance_km.toFixed(2)} km` : '—'}
        </dd>
      </div>
      <div className="min-w-0 sm:col-span-2">
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Address</dt>
        <dd className="mt-0.5 whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100">{row.address || '—'}</dd>
      </div>
    </dl>
  );
}

/**
 * Internal directory scraper UI: search, table, sort, geolocation for distance, PDF/Excel export.
 * Nav link "Scrapper" is shown only to Django staff/superuser (see Navigation.js).
 */
const ScrapperPage = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { showError, showSuccess } = useToast();

  /** Empty default — user must set search type (category); API always receives empty specialization from this page. */
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [area, setArea] = useState('');
  const [extraSearch, setExtraSearch] = useState('');

  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  const [sessionId, setSessionId] = useState(null);
  const [listings, setListings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [backgroundStatus, setBackgroundStatus] = useState('');
  const [lastError, setLastError] = useState('');
  const [sort, setSort] = useState('name_asc');
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchMoreSubmitting, setFetchMoreSubmitting] = useState(false);
  /** Poll listings while background job runs or force-refresh was just queued (response was still "done"). */
  const [forceRefreshQueued, setForceRefreshQueued] = useState(false);
  const [extendExhausted, setExtendExhausted] = useState(false);
  const [statusBanner, setStatusBanner] = useState('');
  /** True when API returned 403 — user is logged in but not staff and not on SCRAPPER_ALLOWED_EMAILS. */
  const [scraperAccessDenied, setScraperAccessDenied] = useState(false);
  /** Fingerprint of last successful search params — skip redundant POST (same search = DB only). */
  const lastSearchKeyRef = useRef('');
  /** Expanded listing row id (mobile cards + desktop table detail row). */
  const [expandedRowId, setExpandedRowId] = useState(null);
  /** Monotonic tick so we re-render while background is running (stall detection). */
  const [backgroundPollTick, setBackgroundPollTick] = useState(0);
  /** When current session entered `running` (for stall timeout UI). */
  const [backgroundRunningSinceMs, setBackgroundRunningSinceMs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await fetchCities();
        if (!cancelled) {
          setCities(c);
          setScraperAccessDenied(false);
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          typeof e?.message === 'string' && e.message.trim()
            ? e.message
            : 'Could not load cities.';
        showError(msg);
        if (e?.type === ErrorTypes.PERMISSION_ERROR) {
          setScraperAccessDenied(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  useEffect(() => {
    let cancelled = false;
    if (!city) {
      setAreas([]);
      setArea('');
      return;
    }
    (async () => {
      try {
        const a = await fetchAreas(city);
        if (!cancelled) setAreas(a);
      } catch {
        if (!cancelled) setAreas([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [city]);

  const refreshListings = useCallback(
    async (sid, overrides = {}) => {
      if (!sid) return;
      const s = overrides.sort ?? sort;
      const p = overrides.page ?? page;
      const ps = overrides.pageSize ?? pageSize;
      try {
        const data = await fetchListings(sid, {
          sort: s,
          userLat,
          userLng,
          page: p,
          pageSize: ps,
        });
        setListings(data.listings || []);
        setPagination(data.pagination || null);
        setBackgroundStatus(data.background_status || '');
        setLastError(data.last_error || '');
        setExtendExhausted(data.extend_exhausted ?? false);
        if (data.background_status !== 'running') {
          setForceRefreshQueued(false);
        }
        if (data.background_status === 'done') {
          setStatusBanner('');
        }
      } catch (e) {
        showError(e?.message || 'Failed to load listings');
      }
    },
    [sort, userLat, userLng, page, pageSize, showError],
  );

  useEffect(() => {
    if (!sessionId) return;
    refreshListings(sessionId);
    // Intentionally omit sessionId: first page of a new search is returned by POST /search (avoids duplicate GET).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when sort, coords, or page changes only
  }, [sort, userLat, userLng, page, pageSize, refreshListings]);

  useEffect(() => {
    if (!sessionId) return;
    if (backgroundStatus !== 'running' && !forceRefreshQueued) return;
    const t = setInterval(() => {
      setBackgroundPollTick((k) => k + 1);
      refreshListings(sessionId);
    }, 4000);
    return () => clearInterval(t);
  }, [sessionId, backgroundStatus, forceRefreshQueued, refreshListings]);

  useEffect(() => {
    setExpandedRowId(null);
  }, [sessionId, page]);

  useEffect(() => {
    setBackgroundRunningSinceMs(null);
  }, [sessionId]);

  useEffect(() => {
    if (backgroundStatus === 'running') {
      setBackgroundRunningSinceMs((prev) => (prev == null ? Date.now() : prev));
    } else {
      setBackgroundRunningSinceMs(null);
    }
  }, [backgroundStatus]);

  const backgroundStalled = useMemo(() => {
    if (backgroundStatus !== 'running') return false;
    if (backgroundRunningSinceMs == null) return false;
    return Date.now() - backgroundRunningSinceMs > BACKGROUND_STALL_MS;
  }, [backgroundStatus, backgroundRunningSinceMs, backgroundPollTick]);

  const showBackgroundUpdating = backgroundStatus === 'running' && !backgroundStalled;

  const emptyListCopy = useMemo(() => {
    if (lastError) {
      return `Could not load results. ${lastError}`;
    }
    const total = pagination?.total_count ?? 0;
    if (total === 0 && backgroundStalled) {
      return 'No listings yet and the background update is taking longer than expected. Run Search again to refresh, or ensure the background worker is running.';
    }
    if (total === 0 && backgroundStatus === 'running' && !backgroundStalled) {
      return 'Loading results…';
    }
    if (total === 0 && backgroundStatus === 'done' && extendExhausted) {
      return 'No listings found for this search, and no more data is available to load.';
    }
    if (total === 0 && backgroundStatus === 'done') {
      return 'No listings found for this search. Try different keywords or another city.';
    }
    if (total === 0) {
      return 'No listings yet. Run Search again or wait for the background refresh.';
    }
    return 'No rows on this page.';
  }, [
    lastError,
    pagination,
    backgroundStalled,
    backgroundStatus,
    extendExhausted,
    backgroundPollTick,
  ]);

  useEffect(() => {
    if (backgroundStalled) {
      setForceRefreshQueued(false);
      setStatusBanner('');
    }
  }, [backgroundStalled]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (scraperAccessDenied) return;
    if (!city.trim()) {
      showError('Please select a city');
      return;
    }
    if (!category.trim()) {
      showError('Enter what to search for (e.g. doctor, dentist, property).');
      return;
    }
    const paramsKey = buildSearchParamsKey(category, city, area, extraSearch);
    if (sessionId && paramsKey === lastSearchKeyRef.current) {
      const totalCount = pagination?.total_count ?? 0;
      if (totalCount < LOW_RESULT_THRESHOLD) {
        setSubmitting(true);
        try {
          setPage(1);
          const data = await searchDirectory({
            category: category.trim(),
            city: city.trim(),
            specialization: '',
            area: area.trim(),
            extra_search: extraSearch.trim(),
            page: 1,
            page_size: pageSize,
            force_refresh: true,
          });
          lastSearchKeyRef.current = paramsKey;
          setSessionId(data.session_id);
          setListings(data.listings || []);
          setPagination(data.pagination || null);
          setBackgroundStatus(data.background_status || '');
          setLastError(data.last_error || '');
          setExtendExhausted(data.extend_exhausted ?? false);
          setForceRefreshQueued(!!data.force_refresh_queued);
          if (data.force_refresh_skipped_reason) {
            setStatusBanner(mapDirectorySkipReason(data.force_refresh_skipped_reason));
          } else if (data.force_refresh_queued) {
            setStatusBanner(
              'Low result count — requesting more from Google Places. List will refresh shortly.',
            );
          }
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'Search failed';
          showError(msg);
        } finally {
          setSubmitting(false);
        }
        return;
      }
      setPage(1);
      setSubmitting(true);
      try {
        await refreshListings(sessionId, { page: 1, pageSize });
      } finally {
        setSubmitting(false);
      }
      showSuccess('Loaded from saved search (no duplicate request).');
      return;
    }

    setSubmitting(true);
    try {
      setPage(1);
      const data = await searchDirectory({
        category: category.trim(),
        city: city.trim(),
        specialization: '',
        area: area.trim(),
        extra_search: extraSearch.trim(),
        page: 1,
        page_size: pageSize,
        force_refresh: false,
      });
      lastSearchKeyRef.current = paramsKey;
      setSessionId(data.session_id);
      setListings(data.listings || []);
      setPagination(data.pagination || null);
      setBackgroundStatus(data.background_status || '');
      setLastError(data.last_error || '');
      setExtendExhausted(data.extend_exhausted ?? false);
      setForceRefreshQueued(!!data.force_refresh_queued);
      setStatusBanner('');
      if (data.cached) {
        showSuccess('Loaded from database (same search — no external scrape).');
      } else {
        showSuccess('Results loaded. More data may arrive in the background.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Search failed';
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFetchMore = async () => {
    if (!sessionId || scraperAccessDenied) return;
    setFetchMoreSubmitting(true);
    try {
      const data = await fetchMoreFromDirectory(sessionId);
      setExtendExhausted(data.extend_exhausted ?? false);
      if (!data.extend_queued) {
        if (data.extend_skipped_reason === 'exhausted') {
          setExtendExhausted(true);
          setStatusBanner('No more distinct results from Google for this search.');
        } else if (data.extend_skipped_reason) {
          showError(mapDirectorySkipReason(data.extend_skipped_reason));
        }
        return;
      }
      setForceRefreshQueued(true);
      setStatusBanner('Fetching more listings …');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Fetch more failed';
      showError(msg);
    } finally {
      setFetchMoreSubmitting(false);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      showError('Geolocation is not supported by this browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setLocating(false);
        showSuccess('Location saved. Click the km column header to sort by distance.');
      },
      () => {
        setLocating(false);
        showError('Could not read your location');
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const handleExport = async (format) => {
    if (!sessionId) return;
    try {
      await downloadExport(sessionId, format, sort, userLat, userLng);
    } catch (e) {
      showError(e?.message || 'Export failed');
    }
  };

  const sortColumnIndicator = (col) => {
    const [asc, desc] = COLUMN_SORT[col];
    if (sort === asc) return ' ↑';
    if (sort === desc) return ' ↓';
    return '';
  };

  const handleColumnSort = (col) => {
    if (col === 'km' && (userLat == null || userLng == null)) {
      showError('Use “Use my location” first to sort by distance.');
      return;
    }
    const [asc, desc] = COLUMN_SORT[col];
    if (sort === asc) setSort(desc);
    else if (sort === desc) setSort(asc);
    else setSort(asc);
    setPage(1);
  };

  const handleMobileSortChange = (nextSort) => {
    if (
      (nextSort === 'distance_asc' || nextSort === 'distance_desc') &&
      (userLat == null || userLng == null)
    ) {
      showError('Use “Use my location” first to sort by distance.');
      return;
    }
    setSort(nextSort);
    setPage(1);
  };

  const toggleRowExpanded = (id) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  if (isLoading) {
    return (
      <PageLayout title="Directory Scraper">
        <div className="min-h-[40vh] flex items-center justify-center text-gray-600 dark:text-gray-300">
          Loading…
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/scrapper' }} />;
  }

  return (
    <PageLayout title="Directory Scraper">
      <Helmet>
        <title>Directory Scraper — PakPoints</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10 sm:pb-12">
        <div className="mx-auto w-full max-w-7xl px-3 py-6 sm:px-4 sm:py-8 lg:px-8">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-2xl">
            Directory scraper
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            Search cached listings; background jobs refresh data. Staff or allowlisted accounts only.
          </p>

          {scraperAccessDenied && (
            <div
              role="alert"
              className="mt-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/25 dark:border-amber-600 px-4 py-3 text-sm text-amber-950 dark:text-amber-100"
            >
              <p className="font-semibold">Why you see this</p>
              <p className="mt-2">
                You are signed in, but this API is restricted. Allow access by either: (1) marking your user as{' '}
                <strong>staff</strong> in Django admin, or (2) adding your email to the backend env var{' '}
                <code className="rounded bg-amber-100 dark:bg-amber-900 px-1 py-0.5 text-xs">
                  SCRAPPER_ALLOWED_EMAILS
                </code>{' '}
                (comma-separated), then redeploy or restart the backend container.
              </p>
            </div>
          )}

          <form
            onSubmit={handleSearch}
            className={`mt-5 grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:mt-6 sm:grid-cols-2 sm:p-6 lg:grid-cols-3 ${scraperAccessDenied ? 'pointer-events-none opacity-60' : ''}`}
          >
            <label className="block">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Search for{' '}
                <span className="font-normal text-gray-500 dark:text-gray-400">(required)</span>
              </span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-400"
                value={category}
                onChange={(ev) => setCategory(ev.target.value)}
                placeholder="e.g. doctor, dentist, property"
                aria-label="Search for"
                required
              />
            </label>
            <SearchableSelect
              id="scrapper-city"
              label="City"
              value={city}
              onChange={(ev) => setCity(ev.target.value)}
              options={cities}
              searchable
              required
              emptyOption={{ value: '', label: 'Select city' }}
              searchPlaceholder="Search cities…"
              className="block"
            />
            <SearchableSelect
              id="scrapper-area"
              label="Area"
              value={area}
              onChange={(ev) => setArea(ev.target.value)}
              options={areas}
              searchable
              disabled={!areas.length}
              emptyOption={{
                value: '',
                label: areas.length ? 'Any / select area' : 'No areas list for this city',
              }}
              searchPlaceholder="Search areas…"
              className="block"
            />
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Extra search terms</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-400"
                value={extraSearch}
                onChange={(ev) => setExtraSearch(ev.target.value)}
                placeholder="Optional keywords"
              />
            </label>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={submitting || scraperAccessDenied}
                className="min-h-[48px] w-full rounded-lg bg-primary-600 px-6 py-3 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60 sm:min-h-0 sm:w-auto sm:py-2"
              >
                {submitting ? 'Searching…' : 'Search'}
              </button>
            </div>
          </form>

          {sessionId && statusBanner && (
            <div
              role="status"
              className="mt-4 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-950 dark:border-primary-700 dark:bg-primary-900/25 dark:text-primary-100"
            >
              {statusBanner}
            </div>
          )}

          {sessionId && (
            <div className="mt-5 flex flex-col gap-3 sm:mt-6 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <div className="order-1 flex min-h-[1.25rem] flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                {showBackgroundUpdating && (
                  <span className="text-primary-600 dark:text-primary-400">Updating in background…</span>
                )}
                {backgroundStalled && (
                  <span className="text-amber-800 dark:text-amber-200">
                    Background update is taking longer than expected. Run Search again to refresh.
                  </span>
                )}
                {extendExhausted && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {pagination && pagination.total_count > 0
                      ? 'No more data to load for this search.'
                      : 'No more data available for this search.'}
                  </span>
                )}
                {lastError && (
                  <span className="text-red-600 dark:text-red-400">Error: {lastError}</span>
                )}
              </div>
              <label className="order-2 flex min-h-[44px] flex-col gap-1 sm:order-3 md:hidden">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Sort results</span>
                <select
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100"
                  value={sort}
                  onChange={(ev) => handleMobileSortChange(ev.target.value)}
                  aria-label="Sort results"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <span className="order-3 hidden text-xs text-gray-500 dark:text-gray-400 md:inline sm:order-2">
                Sort: click column headers (toggle ↑ ↓)
              </span>
              <div className="order-4 flex flex-wrap items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={requestLocation}
                  disabled={locating}
                  className="min-h-[44px] min-w-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 dark:hover:bg-gray-800 sm:min-h-0 sm:min-w-0 sm:py-1"
                >
                  {locating ? 'Locating…' : 'Use my location'}
                </button>
                {userLat != null && userLng != null && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {userLat.toFixed(4)}, {userLng.toFixed(4)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleExport('xlsx')}
                  className="min-h-[44px] rounded-lg bg-green-600 px-4 py-2.5 text-sm text-white hover:bg-green-700 sm:min-h-0 sm:py-1"
                >
                  Excel
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('pdf')}
                  className="min-h-[44px] rounded-lg bg-red-600 px-4 py-2.5 text-sm text-white hover:bg-red-700 sm:min-h-0 sm:py-1"
                >
                  PDF
                </button>
              </div>
            </div>
          )}

          {sessionId && pagination && (
            <div
              className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-4"
              aria-live="polite"
            >
              <div className="min-w-0">
                <span className="font-medium text-gray-900 dark:text-white">Results: </span>
                {pagination.total_count === 0 ? (
                  <span>
                    0 total
                    {backgroundStatus === 'done' && !lastError && (
                      <span className="mt-1 block text-xs font-normal text-gray-500 dark:text-gray-400">
                        {extendExhausted
                          ? 'No more data available for this search.'
                          : 'No listings match this search.'}
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    <span>{pagination.total_count} total</span>
                    <span className="mx-2 text-gray-400">·</span>
                    <span>
                      page {pagination.page} of {pagination.total_pages}
                    </span>
                    <span className="mx-2 text-gray-400">·</span>
                    <span>
                      {pagination.results_on_page} on this page
                    </span>
                    <span className="mx-2 text-gray-400">·</span>
                    <span>{pagination.page_size} per page</span>
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <label className="flex min-h-[44px] items-center gap-2 text-xs sm:text-sm">
                  <span className="whitespace-nowrap">Per page</span>
                  <select
                    className="min-h-[44px] rounded border border-gray-300 bg-white px-2 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 sm:min-h-0 sm:py-1"
                    value={pageSize}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setPageSize(n);
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!pagination.total_count || pagination.page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="min-h-[44px] min-w-[44px] rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-900 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 sm:min-h-0 sm:min-w-0 sm:py-1 sm:text-sm"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={
                      !pagination.total_count ||
                      pagination.page >= pagination.total_pages
                    }
                    onClick={() => setPage((p) => p + 1)}
                    className="min-h-[44px] min-w-[44px] rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-900 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-100 sm:min-h-0 sm:min-w-0 sm:py-1 sm:text-sm"
                  >
                    Next
                  </button>
                </div>
                {pagination.total_count > 0 &&
                  pagination.page === pagination.total_pages &&
                  !extendExhausted &&
                  backgroundStatus !== 'running' && (
                    <button
                      type="button"
                      onClick={handleFetchMore}
                      disabled={fetchMoreSubmitting || scraperAccessDenied}
                      className="min-h-[44px] w-full rounded-lg border border-primary-500 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-900 hover:bg-primary-100 disabled:opacity-50 dark:border-primary-600 dark:bg-primary-900/30 dark:text-primary-100 dark:hover:bg-primary-900/50 sm:w-auto"
                    >
                      {fetchMoreSubmitting ? 'Fetching…' : 'Fetch More'}
                    </button>
                  )}
                {pagination.total_count > 0 &&
                  pagination.page === pagination.total_pages &&
                  extendExhausted && (
                    <p className="w-full text-xs text-gray-500 dark:text-gray-400">
                      No more distinct listings from Google for this search. Try different keywords or use Refresh
                      (low results) from Search.
                    </p>
                  )}
              </div>
            </div>
          )}

          {sessionId && (
            <>
              {/* Mobile-first: card list with expandable full details */}
              <div className="mt-4 space-y-3 md:hidden">
                {listings.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {emptyListCopy}
                  </div>
                ) : (
                  listings.map((row) => {
                    const expanded = expandedRowId === row.id;
                    return (
                      <article
                        key={row.id}
                        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
                      >
                        <button
                          type="button"
                          onClick={() => toggleRowExpanded(row.id)}
                          aria-expanded={expanded}
                          className="flex min-h-[56px] w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-gray-900/50"
                        >
                          <span
                            className="mt-0.5 shrink-0 text-gray-400"
                            aria-hidden="true"
                          >
                            {expanded ? '▼' : '▶'}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{row.name_display}</span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                              {[row.area, row.city].filter(Boolean).join(' · ') || '—'}
                              {row.distance_km != null && (
                                <span className="ml-1">· {row.distance_km.toFixed(1)} km</span>
                              )}
                            </span>
                          </span>
                        </button>
                        {expanded && (
                          <div
                            className="border-t border-gray-100 px-4 py-4 dark:border-gray-700"
                            id={`listing-detail-${row.id}`}
                          >
                            <ListingDetailFields row={row} />
                          </div>
                        )}
                      </article>
                    );
                  })
                )}
              </div>

              {/* md+: table with expandable detail row (full address / phone) */}
              <div className="mt-4 hidden overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 md:block">
                <table className="min-w-[720px] w-full text-sm lg:min-w-0">
                  <thead className="bg-gray-100 dark:bg-gray-900/80">
                    <tr>
                      <th
                        scope="col"
                        className="w-12 px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
                      >
                        <span className="sr-only">Expand row</span>
                      </th>
                      {[
                        { id: 'name', label: 'Name' },
                        { id: 'area', label: 'Area' },
                        { id: 'city', label: 'City' },
                        { id: 'phone', label: 'Phone' },
                        { id: 'address', label: 'Address' },
                        { id: 'km', label: 'km' },
                      ].map((col) => (
                        <th
                          key={col.id}
                          scope="col"
                          className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-200"
                        >
                          <button
                            type="button"
                            aria-label={`Sort by ${col.label}, toggle ascending or descending`}
                            onClick={() => handleColumnSort(col.id)}
                            className="inline-flex max-w-full items-center gap-0.5 rounded px-1 py-1 text-left font-semibold text-gray-800 hover:bg-gray-200 dark:text-gray-100 dark:hover:bg-gray-700"
                          >
                            <span className="truncate">{col.label}</span>
                            <span className="shrink-0 text-primary-600" aria-hidden="true">
                              {sortColumnIndicator(col.id)}
                            </span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listings.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-gray-500 dark:text-gray-300">
                          {emptyListCopy}
                        </td>
                      </tr>
                    ) : (
                      listings.map((row) => {
                        const expanded = expandedRowId === row.id;
                        return (
                          <React.Fragment key={row.id}>
                            <tr className="border-t border-gray-100 dark:border-gray-700">
                              <td className="px-2 py-2 align-top">
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(row.id)}
                                  aria-expanded={expanded}
                                  aria-controls={`listing-detail-desktop-${row.id}`}
                                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                                  title={expanded ? 'Collapse details' : 'Expand details'}
                                >
                                  <span aria-hidden="true">{expanded ? '▼' : '▶'}</span>
                                </button>
                              </td>
                              <td className="max-w-[12rem] px-3 py-2 align-top font-medium text-gray-900 dark:text-gray-100">
                                <span className="line-clamp-2">{row.name_display}</span>
                              </td>
                              <td className="max-w-[8rem] px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                <span className="line-clamp-2 break-words">{row.area || '—'}</span>
                              </td>
                              <td className="max-w-[8rem] px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                <span className="line-clamp-2 break-words">{row.city || '—'}</span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                {row.phone || '—'}
                              </td>
                              <td className="max-w-xs px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                <span className="line-clamp-2 break-words" title={row.address}>
                                  {row.address || '—'}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                {row.distance_km != null ? row.distance_km.toFixed(2) : '—'}
                              </td>
                            </tr>
                            {expanded && (
                              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
                                <td colSpan={7} className="px-4 py-4" id={`listing-detail-desktop-${row.id}`}>
                                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Full details
                                  </p>
                                  <ListingDetailFields row={row} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default ScrapperPage;
